<script setup lang="ts">
import { useRouter } from 'vue-router'
import BackendStatus from '@/components/BackendStatus.vue'
import { useAuthStore } from '@/stores/auth'

const auth = useAuthStore()
const router = useRouter()

async function logout(): Promise<void> {
  await auth.logout()
  router.push({ name: 'login' })
}
</script>

<template>
  <div class="app-shell">
    <header class="app-header">
      <div class="header-inner container">
        <RouterLink to="/library" class="brand">
          <span class="brand-mark">▚</span>
          <span class="brand-name">Document Scanner</span>
        </RouterLink>
        <div class="header-actions">
          <RouterLink to="/search" class="search-link" aria-label="Search">🔍</RouterLink>
          <BackendStatus />
          <button
            v-if="auth.isAuthenticated"
            class="logout"
            type="button"
            :title="`Sign out (${auth.username})`"
            aria-label="Sign out"
            @click="logout"
          >
            ⏻
          </button>
        </div>
      </div>
    </header>

    <main>
      <slot />
    </main>
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--border);
  padding-top: env(safe-area-inset-top);
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text);
  font-weight: 700;
}

.brand-mark {
  color: var(--primary);
  font-size: 1.25rem;
}

.brand-name {
  font-size: 1rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.search-link {
  font-size: 1.1rem;
  text-decoration: none;
  padding: 0.25rem;
}

.logout {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-muted);
  border-radius: 8px;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  font-size: 0.95rem;
  line-height: 1;
}

.logout:hover {
  color: var(--text);
  border-color: var(--primary);
}
</style>
